import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaUserTie,
  FaPenNib,
  FaBookOpen,
  FaUniversity,
  FaMicrophoneAlt,
  FaGraduationCap,
  FaArrowRight,
  FaPhoneAlt,
  FaChurch,
  FaSchool,
  FaTheaterMasks,
  FaUsers,
  FaCompass,
  FaHandshake,
  FaLightbulb,
} from 'react-icons/fa';
import { GiMusicalScore, GiBaton } from 'react-icons/gi';
import { useSettings } from '../context/SettingsContext';
import './Consultancy.css';

const services = [
  {
    id: 'music-directing',
    num: '01',
    icon: GiBaton,
    title: 'Music Directing',
    description:
      'Bring your ensemble to life with experienced music direction. From shaping interpretation and balance to drawing out the very best from every performer, our directors lead with clarity, musicianship, and vision — whether for a single concert or an ongoing season.',
    subServices: [
      {
        title: 'Orchestra & Ensemble Directing',
        text: 'Conducting and artistic direction for symphony orchestras, chamber groups, jazz ensembles, and bands of every size.',
      },
      {
        title: 'Choir Conducting & Directing',
        text: 'Choir leadership for churches, schools, community choirs, and professional vocal ensembles, covering rehearsal craft, repertoire, and performance.',
      },
    ],
  },
  {
    id: 'composing-arrangement',
    num: '02',
    icon: FaPenNib,
    title: 'Music Composing & Arrangement',
    description:
      'Original music written and arranged for the moment. Whether you need a custom piano work, a fully orchestrated piece, or a stage-worthy musical narrative, we craft music that fits your vision, performers, and audience.',
    subServices: [
      {
        title: 'Piano Composition & Arranging',
        text: 'Solo, duet, and accompanied piano works composed or arranged from scratch, including idiomatic transcriptions of existing pieces.',
      },
      {
        title: 'Orchestral / Ensemble Composition',
        text: 'Full scores for orchestras, ensembles, and bands — from short overtures to extended multi-movement works.',
      },
      {
        title: 'Opera & Musical Play Writing',
        text: 'Libretto, score, and musical dramaturgy for operas, musical theatre, and stage productions of all scales.',
      },
    ],
  },
  {
    id: 'music-advisory',
    num: '03',
    icon: FaLightbulb,
    title: 'Music Advisory',
    description:
      'Strategic, artistic, and operational guidance for music organizations. Whether you\'re launching a new ensemble, refreshing your repertoire direction, or planning a major artistic shift, we serve as a trusted advisor every step of the way.',
    subServices: [
      {
        title: 'Creative & Artistic Direction Consultancy',
        text: 'Long-term creative planning, season programming, and artistic identity development for music brands and venues.',
      },
      {
        title: 'Orchestra & Ensemble Consultancy',
        text: 'Structural, repertoire, and audition-process advisory for orchestras, choirs, and ensembles seeking to elevate their craft.',
      },
    ],
  },
  {
    id: 'classical-art-music',
    num: '04',
    icon: FaUniversity,
    title: 'Classical / Art Music Consultancy',
    description:
      'Deep expertise in the European classical tradition and African art music. We design and deliver targeted training, knowledge-sharing seminars, and facilitation services that elevate the practice of classical music in Nigeria and beyond.',
    subServices: [
      {
        title: 'Specialised Trainings',
        text: 'Custom curricula in music theory, performance practice, score study, and historically-informed performance.',
      },
      {
        title: 'Seminar Organizing',
        text: 'End-to-end management of academic and professional seminars — speakers, agenda, logistics, and post-event reporting.',
      },
      {
        title: 'Seminar Facilitating',
        text: 'Expert moderation and content delivery for music workshops, masterclasses, and panel discussions.',
      },
    ],
  },
  {
    id: 'performance-consultancy',
    num: '05',
    icon: FaMicrophoneAlt,
    title: 'Performance Consultancy',
    description:
      'From stagecraft to full concert production — we coach performers, prepare them for the platform, and manage the production process end-to-end so that every moment on stage feels effortless and unforgettable.',
    subServices: [
      {
        title: 'Performance Training',
        text: 'One-on-one and group coaching covering stage presence, audition preparation, performance anxiety, and interpretation.',
      },
      {
        title: 'Concert Production',
        text: 'Programming, venue selection, technical management, marketing, and run-of-show coordination for live music events.',
      },
    ],
  },
  {
    id: 'educational-training',
    num: '06',
    icon: FaGraduationCap,
    title: 'Educational & Training Consultancy',
    description:
      'Music education that lasts. We partner with schools, conservatories, churches, and private studios to design curricula, develop teachers, and build sustainable music programs that produce confident, well-rounded musicians at every level.',
    subServices: [
      {
        title: 'Curriculum Design',
        text: 'Tailored music syllabi for primary, secondary, and tertiary institutions, aligned with national and international standards.',
      },
      {
        title: 'Teacher Training & Mentorship',
        text: 'Pedagogical workshops and ongoing mentorship for music teachers and instructors looking to sharpen their craft.',
      },
      {
        title: 'Programme Development',
        text: 'Building music departments and ensemble programs from the ground up — from instrument procurement to pedagogy.',
      },
    ],
  },
];

const approach = [
  {
    num: '1',
    title: 'Listen & understand',
    description:
      'Every engagement begins with a deep conversation about your goals, your performers, and your audience. No two projects are alike.',
  },
  {
    num: '2',
    title: 'Plan with precision',
    description:
      'We translate vision into a clear, actionable roadmap — milestones, timelines, deliverables, and accountability built in from day one.',
  },
  {
    num: '3',
    title: 'Deliver with excellence',
    description:
      'From rehearsal room to concert hall, from sketchbook to score, every step is guided by craftsmanship and a commitment to your success.',
  },
];

const audiences = [
  { icon: FaUsers, label: 'Orchestras & Ensembles' },
  { icon: FaChurch, label: 'Churches & Choirs' },
  { icon: FaSchool, label: 'Schools & Conservatories' },
  { icon: FaTheaterMasks, label: 'Theatre Productions' },
  { icon: FaUserTie, label: 'Solo Performers' },
  { icon: GiMusicalScore, label: 'Music Publishers' },
  { icon: FaHandshake, label: 'Cultural Organizations' },
  { icon: FaCompass, label: 'Independent Artists' },
];

const Consultancy = () => {
  const { settings } = useSettings();

  return (
    <div className="consultancy-page">
      {/* Hero */}
      <section className="consultancy-hero">
        <div className="container consultancy-hero-content">
          <span className="hero-eyebrow">Music Consultancy Services</span>
          <h1>
            Expert guidance for the<br />
            <em>art of music making</em>
          </h1>
          <p>
            From the conductor's podium to the composer's studio, from classroom curricula to
            concert production — Centre Techniques Pianos partners with musicians, ensembles, and
            institutions to lift their work to its highest artistic standard.
          </p>
          <div className="consultancy-hero-cta">
            <Link to="/contact" className="btn btn-primary btn-lg">
              Request a consultation <FaArrowRight />
            </Link>
            {settings?.contactPhone && (
              <a href={`tel:${settings.contactPhone}`} className="btn btn-secondary btn-lg">
                <FaPhoneAlt /> {settings.contactPhone}
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Service intro */}
      <div className="container">
        <div className="consultancy-intro">
          <span className="section-tag">Our Specializations</span>
          <h2>Six pillars of music consultancy</h2>
          <p>
            Each service blends artistic insight with practical experience, delivered by professionals
            who have stood on the podium, written the score, and led the rehearsal.
          </p>
        </div>

        {/* Services grid */}
        <div className="consultancy-grid">
          {services.map((service) => {
            const Icon = service.icon;
            return (
              <article key={service.id} id={service.id} className="consultancy-card">
                <div className="card-header-row">
                  <div className="card-icon">
                    <Icon />
                  </div>
                  <div>
                    <span className="card-num">Service {service.num}</span>
                  </div>
                </div>

                <h3>{service.title}</h3>
                <p>{service.description}</p>

                <ul className="sub-services">
                  {service.subServices.map((sub, i) => (
                    <li key={i}>
                      <span className="sub-marker">{String.fromCharCode(97 + i)}</span>
                      <div className="sub-content">
                        <strong>{sub.title}</strong>
                        <span>{sub.text}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </div>

      {/* Approach */}
      <section className="approach-section">
        <div className="container">
          <div className="consultancy-intro" style={{ margin: 0 }}>
            <span className="section-tag">How We Work</span>
            <h2>A thoughtful, structured approach</h2>
            <p>Every consultancy engagement follows three guiding principles, refined over years of practice.</p>
          </div>

          <div className="approach-grid">
            {approach.map((step, i) => (
              <div key={i} className="approach-item">
                <div className="approach-num">{step.num}</div>
                <h4>{step.title}</h4>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Audience */}
      <section className="audience-section">
        <div className="container">
          <div className="consultancy-intro" style={{ margin: 0 }}>
            <span className="section-tag">Who We Serve</span>
            <h2>Built for every kind of music maker</h2>
            <p>Our consultancy services are designed for individuals and institutions across the music ecosystem.</p>
          </div>

          <div className="audience-grid">
            {audiences.map(({ icon: Icon, label }) => (
              <div key={label} className="audience-tag">
                <Icon />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="consultancy-cta">
        <div className="container">
          <div className="consultancy-cta-card">
            <h2>Let's create something extraordinary</h2>
            <p>
              Whether you're conducting your first concert, scoring your first opera, or building
              a music program from scratch, our team is ready to partner with you. Book a no-obligation
              consultation to discuss your goals.
            </p>
            <div className="consultancy-cta-buttons">
              <Link to="/contact" className="btn btn-primary btn-lg">
                Book a consultation <FaArrowRight />
              </Link>
              <Link to="/services" className="btn btn-secondary btn-lg">
                <FaBookOpen /> Other services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Consultancy;
